import React, { useState, useEffect } from 'react';
import { ChevronDown, ChevronRight, Target, Star, Calendar, AlertCircle, Plus, Eye, Heart, Lightbulb } from 'lucide-react';
import type { Year } from '../types';
import { yearsAPI } from '../services/api';
import ReviewModal from './ReviewModal';
import { useNotification } from '../hooks/useNotification';

interface YearViewProps {
  selectedTeamId?: string | null;
  onAddTodo: () => void;
  onStartYearlyReview: () => void;
}

const YearView: React.FC<YearViewProps> = ({
  selectedTeamId,
  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  onAddTodo,
  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  onStartYearlyReview
}) => {
  const [currentYear, setCurrentYear] = useState<Year | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [isGoalsExpanded, setIsGoalsExpanded] = useState(true);
  const [isProjectsExpanded, setIsProjectsExpanded] = useState(true);
  const [isValuesExpanded, setIsValuesExpanded] = useState(true);
  const [isVisionExpanded, setIsVisionExpanded] = useState(true);
  const [showYearlyReview, setShowYearlyReview] = useState(false);
  const [newProjectText, setNewProjectText] = useState('');

  const { showNotification } = useNotification();

  // Check for pending review from widget navigation
  useEffect(() => {
    const pending = sessionStorage.getItem('pendingReview');
    if (pending === 'yearly') {
      sessionStorage.removeItem('pendingReview');
      setShowYearlyReview(true);
    }
  }, []);

  useEffect(() => {
    loadCurrentYear();
  }, [selectedTeamId]);

  const loadCurrentYear = async () => {
    try {
      setIsLoading(true);
      setError(null);
      
      // Get current year
      const year = await yearsAPI.getCurrent(selectedTeamId || undefined);
      setCurrentYear(year);
      
    } catch (err) {
      setError('Failed to load year data');
      console.error('Error loading year:', err);
    } finally {
      setIsLoading(false);
    }
  };

  const getYearlyReviewStatus = () => {
    if (!currentYear) return null;
    
    const now = new Date();
    const yearEnd = new Date(currentYear.end_date);
    const isYearOver = now > yearEnd;
    const isOverdue = isYearOver && !currentYear.is_review_completed;
    
    // For yearly reviews, also consider if we're in the last month of the year
    const lastMonth = new Date(currentYear.year, 11, 1); // December 1st
    const shouldPromptReview = now >= lastMonth && !currentYear.is_review_completed;
    
    return {
      isCompleted: currentYear.is_review_completed,
      isOverdue,
      shouldGlow: isOverdue || shouldPromptReview
    };
  };

  const handleUpdateYear = async (updates: Partial<Year>) => {
    if (!currentYear) return;
    
    try {
      await yearsAPI.update(currentYear.id, updates);
      await loadCurrentYear(); // Reload year data
      showNotification('success', 'Year updated successfully!');
    } catch (error) {
      console.error('Failed to update year:', error);
      showNotification('error', 'Failed to update year. Please try again.');
    }
  };

  const handleMarkReviewComplete = async () => {
    if (!currentYear) return;
    
    try {
      await yearsAPI.completeReview(currentYear.id);
      await loadCurrentYear(); // Reload year data
      showNotification('success', 'Yearly review completed successfully!');
    } catch (error) {
      console.error('Failed to complete review:', error);
      showNotification('error', 'Failed to complete review. Please try again.');
    }
  };

  const handleAddProject = async () => {
    if (!currentYear || !newProjectText.trim()) return;
    await handleUpdateYear({ projects: [...currentYear.projects, newProjectText.trim()] });
    setNewProjectText('');
  };

  const reviewStatus = getYearlyReviewStatus();

  if (isLoading) {
    return (
      <div className="space-y-6">
        <div className="animate-pulse">
          <div className="h-8 bg-gray-200 rounded-lg mb-4"></div>
          <div className="space-y-3">
            <div className="h-4 bg-gray-200 rounded"></div>
            <div className="h-4 bg-gray-200 rounded"></div>
            <div className="h-4 bg-gray-200 rounded"></div>
          </div>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="space-y-6">
        <div className="bg-red-50 border border-red-200 rounded-lg p-4">
          <div className="flex items-center">
            <AlertCircle className="w-5 h-5 text-red-500 mr-2" />
            <span className="text-red-700">{error}</span>
          </div>
        </div>
      </div>
    );
  }

  if (!currentYear) {
    return (
      <div className="space-y-6">
        <div className="text-center py-8">
          <Calendar className="w-12 h-12 text-gray-400 mx-auto mb-4" />
          <p className="text-gray-600">No year data available</p>
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Year Header */}
      <div className="bg-gradient-to-r from-purple-50 to-indigo-50 border border-purple-200 rounded-xl p-6">
        <div className="flex items-center justify-between mb-4">
          <div className="flex items-center space-x-3">
            <Target className="w-6 h-6 text-purple-600" />
            <h1 className="text-2xl font-bold text-gray-900">
              {currentYear.year} Vision & Goals
            </h1>
          </div>
          
          <div className="flex items-center space-x-3">
            {reviewStatus && (
              <button
                onClick={() => setShowYearlyReview(true)}
                className={`
                  inline-flex items-center px-4 py-2 rounded-lg text-sm font-medium transition-all
                  ${reviewStatus.isCompleted 
                    ? 'bg-green-100 text-green-800 border border-green-200' 
                    : reviewStatus.shouldGlow 
                      ? 'bg-orange-100 text-orange-800 border border-orange-200 animate-pulse shadow-lg' 
                      : 'bg-purple-100 text-purple-800 border border-purple-200 hover:bg-purple-200'
                  }
                `}
              >
                <Star className="w-4 h-4 mr-2" />
                {reviewStatus.isCompleted ? 'Review Complete' : 'Yearly Review'}
                {reviewStatus.isOverdue && <AlertCircle className="w-4 h-4 ml-2" />}
              </button>
            )}
          </div>
        </div>

        {/* Year Stats */}
        <div className="grid grid-cols-4 gap-4">
          <div className="text-center">
            <div className="text-2xl font-bold text-purple-600">{currentYear.goals.length}</div>
            <div className="text-sm text-gray-600">Yearly Goals</div>
          </div>
          <div className="text-center">
            <div className="text-2xl font-bold text-blue-600">{currentYear.projects.length}</div>
            <div className="text-sm text-gray-600">Key Projects</div>
          </div>
          <div className="text-center">
            <div className="text-2xl font-bold text-red-600">{currentYear.values.length}</div>
            <div className="text-sm text-gray-600">Core Values</div>
          </div>
          <div className="text-center">
            <div className="text-2xl font-bold text-green-600">{currentYear.vision ? 1 : 0}</div>
            <div className="text-sm text-gray-600">Vision Set</div>
          </div>
        </div>
      </div>

      {/* Vision Section */}
      <div className="bg-white rounded-xl border-2 border-gray-200 hover:border-gray-300 transition-all duration-200">
        <div className="p-6">
          <div className="flex items-center justify-between mb-4">
            <button
              onClick={() => setIsVisionExpanded(!isVisionExpanded)}
              className="flex items-center space-x-2 text-lg font-semibold text-gray-900 hover:text-purple-600 transition-colors"
            >
              {isVisionExpanded ? (
                <ChevronDown className="w-5 h-5" />
              ) : (
                <ChevronRight className="w-5 h-5" />
              )}
              <Eye className="w-5 h-5 text-purple-600" />
              <span>Vision Statement</span>
            </button>
          </div>

          {isVisionExpanded && (
            <div className="space-y-3">
              {currentYear.vision ? (
                <div className="p-4 bg-purple-50 rounded-lg border border-purple-200">
                  <p className="text-gray-800 leading-relaxed italic">
                    "{currentYear.vision}"
                  </p>
                </div>
              ) : (
                <div className="text-center py-8 text-gray-500">
                  <Eye className="w-8 h-8 mx-auto mb-2 opacity-50" />
                  <p>No vision statement defined</p>
                  <p className="text-sm">Use the yearly review to set your vision</p>
                </div>
              )}
            </div>
          )}
        </div>
      </div>

      {/* Core Values Section */}
      <div className="bg-white rounded-xl border-2 border-gray-200 hover:border-gray-300 transition-all duration-200">
        <div className="p-6">
          <div className="flex items-center justify-between mb-4">
            <button
              onClick={() => setIsValuesExpanded(!isValuesExpanded)}
              className="flex items-center space-x-2 text-lg font-semibold text-gray-900 hover:text-red-600 transition-colors"
            >
              {isValuesExpanded ? (
                <ChevronDown className="w-5 h-5" />
              ) : (
                <ChevronRight className="w-5 h-5" />
              )}
              <Heart className="w-5 h-5 text-red-600" />
              <span>Core Values ({currentYear.values.length})</span>
            </button>
          </div>

          {isValuesExpanded && (
            <div className="space-y-3">
              {currentYear.values.length === 0 ? (
                <div className="text-center py-8 text-gray-500">
                  <Heart className="w-8 h-8 mx-auto mb-2 opacity-50" />
                  <p>No core values defined</p>
                  <p className="text-sm">Use the yearly review to define your values</p>
                </div>
              ) : (
                <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                  {currentYear.values.map((value, index) => (
                    <div key={index} className="p-3 bg-red-50 rounded-lg border border-red-200">
                      <div className="flex items-center">
                        <Heart className="w-4 h-4 text-red-600 mr-2 flex-shrink-0" />
                        <span className="text-gray-800 font-medium">{value}</span>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>
          )}
        </div>
      </div>

      {/* Yearly Goals Section */}
      <div className="bg-white rounded-xl border-2 border-gray-200 hover:border-gray-300 transition-all duration-200">
        <div className="p-6">
          <div className="flex items-center justify-between mb-4">
            <button
              onClick={() => setIsGoalsExpanded(!isGoalsExpanded)}
              className="flex items-center space-x-2 text-lg font-semibold text-gray-900 hover:text-purple-600 transition-colors"
            >
              {isGoalsExpanded ? (
                <ChevronDown className="w-5 h-5" />
              ) : (
                <ChevronRight className="w-5 h-5" />
              )}
              <Target className="w-5 h-5 text-purple-600" />
              <span>Yearly Goals ({currentYear.goals.length})</span>
            </button>
          </div>

          {isGoalsExpanded && (
            <div className="space-y-3">
              {currentYear.goals.length === 0 ? (
                <div className="text-center py-8 text-gray-500">
                  <Target className="w-8 h-8 mx-auto mb-2 opacity-50" />
                  <p>No yearly goals defined</p>
                  <p className="text-sm">Use the yearly review to set your goals</p>
                </div>
              ) : (
                <div className="space-y-2">
                  {currentYear.goals.map((goal, index) => (
                    <div key={index} className="p-3 bg-purple-50 rounded-lg border border-purple-200">
                      <div className="flex items-center">
                        <Target className="w-4 h-4 text-purple-600 mr-2 flex-shrink-0" />
                        <span className="text-gray-800">{goal}</span>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>
          )}
        </div>
      </div>

      {/* Key Projects Section */}
      <div className="bg-white rounded-xl border-2 border-gray-200 hover:border-gray-300 transition-all duration-200">
        <div className="p-6">
          <div className="flex items-center justify-between mb-4">
            <button
              onClick={() => setIsProjectsExpanded(!isProjectsExpanded)}
              className="flex items-center space-x-2 text-lg font-semibold text-gray-900 hover:text-blue-600 transition-colors"
            >
              {isProjectsExpanded ? (
                <ChevronDown className="w-5 h-5" />
              ) : (
                <ChevronRight className="w-5 h-5" />
              )}
              <Lightbulb className="w-5 h-5 text-blue-600" />
              <span>Key Projects ({currentYear.projects.length})</span>
            </button>
            
            <div className="flex items-center gap-2">
              <input
                type="text"
                value={newProjectText}
                onChange={(e) => setNewProjectText(e.target.value)}
                onKeyDown={(e) => e.key === 'Enter' && handleAddProject()}
                placeholder="New project..."
                className="px-3 py-2 text-sm border border-blue-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-300"
              />
              <button
                onClick={handleAddProject}
                disabled={!newProjectText.trim()}
                className="inline-flex items-center px-3 py-2 text-sm font-medium text-blue-600 bg-blue-50 rounded-lg hover:bg-blue-100 transition-colors disabled:opacity-50"
              >
                <Plus className="w-4 h-4 mr-1" />
                Add
              </button>
            </div>
          </div>

          {isProjectsExpanded && (
            <div className="space-y-3">
              {currentYear.projects.length === 0 ? (
                <div className="text-center py-8 text-gray-500">
                  <Lightbulb className="w-8 h-8 mx-auto mb-2 opacity-50" />
                  <p>No key projects defined</p>
                  <p className="text-sm">Use the yearly review to define your major projects</p>
                </div>
              ) : (
                <div className="space-y-2">
                  {currentYear.projects.map((project, index) => (
                    <div key={index} className="p-3 bg-blue-50 rounded-lg border border-blue-200">
                      <div className="flex items-center">
                        <Lightbulb className="w-4 h-4 text-blue-600 mr-2 flex-shrink-0" />
                        <span className="text-gray-800">{project}</span>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>
          )}
        </div>
      </div>

      {/* Yearly Review Modal */}
      {currentYear && (
        <ReviewModal
          isOpen={showYearlyReview}
          onClose={() => setShowYearlyReview(false)}
          reviewType="yearly"
          periodLabel={String(currentYear.year)}
          reflections={currentYear.reflections}
          goals={currentYear.goals}
          vision={currentYear.vision}
          values={currentYear.values}
          onSave={(data) => handleUpdateYear({ ...data, is_review_completed: false })}
          onComplete={(data) => {
            handleUpdateYear({ ...data, is_review_completed: true });
            handleMarkReviewComplete();
          }}
        />
      )}
    </div>
  );
};

export default YearView;